/**
 * PersonalizadorPreviewCanvas
 * Renderiza bandera y nombre en un canvas superpuesto sobre la imagen target de la PDP.
 */
class PersonalizadorPreviewCanvas {
  constructor() {
    this.states = [];
    this.resizeScheduled = false;
    this.flagImageCache = new Map();
    this.logoImageCache = new Map();

    this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
    this.handleSlideshowSelect = this.handleSlideshowSelect.bind(this);
    this.handleResize = this.handleResize.bind(this);

    this.bindAllBlocks();

    document.addEventListener('variant:update', this.handleVariantUpdate);
    document.addEventListener('slideshow:select', this.handleSlideshowSelect, true);
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('orientationchange', this.handleResize);
  }

  bindAllBlocks() {
    const blocks = document.querySelectorAll('.personalizador__block--nombre[data-preview-target-index]');
    blocks.forEach((block) => this.bindBlock(block));
  }

  bindBlock(block) {
    if (!(block instanceof HTMLElement)) return;
    if (block.dataset.previewBound === 'true') return;

    const wrapper = block.closest('.personalizador-wrapper');
    if (!(wrapper instanceof HTMLElement)) return;

    const nameInput = block.querySelector('[data-personalizador-visible-property]');
    if (!(nameInput instanceof HTMLInputElement || nameInput instanceof HTMLTextAreaElement)) return;

    const state = {
      block,
      wrapper,
      nameInput,
      gallery: null,
      layer: null,
      canvas: null,
      targetContainer: null,
      targetImage: null,
      referenceImageWidth: null,
      hasAutoScrolled: false,
      debounceTimer: null,
      onImageLoad: () => this.render(state),
      onInput: () => this.scheduleDebouncedRender(state),
      onImmediate: () => this.renderNow(state),
      onActivateTarget: (event) => this.handleInteractiveTargetSync(state, event),
      onModeChange: (event) => this.handleModeChange(state, event),
      onUploadSuccess: () => this.scheduleDebouncedRender(state),
      onUploadSelected: () => this.scheduleDebouncedRender(state),
    };

    state.wrapper.addEventListener('input', state.onInput);
    state.wrapper.addEventListener('change', state.onImmediate);
    state.wrapper.addEventListener('personalizador:preview-sync', state.onInput);
    state.wrapper.addEventListener('personalizador:mode-change', state.onModeChange);
    state.wrapper.addEventListener('image-upload:success', state.onUploadSuccess);
    state.wrapper.addEventListener('image-upload:selected', state.onUploadSelected);
    state.wrapper.addEventListener('focusin', state.onActivateTarget);
    state.wrapper.addEventListener('pointerdown', state.onActivateTarget);

    state.nameInput.addEventListener('blur', state.onImmediate);

    state.block.dataset.previewBound = 'true';
    this.states.push(state);

    this.refreshTarget(state, { allowAutoScroll: true });
    this.render(state);
  }

  unbindState(state) {
    window.clearTimeout(state.debounceTimer);

    state.wrapper.removeEventListener('input', state.onInput);
    state.wrapper.removeEventListener('change', state.onImmediate);
    state.wrapper.removeEventListener('personalizador:preview-sync', state.onInput);
    state.wrapper.removeEventListener('personalizador:mode-change', state.onModeChange);
    state.wrapper.removeEventListener('image-upload:success', state.onUploadSuccess);
    state.wrapper.removeEventListener('image-upload:selected', state.onUploadSelected);
    state.wrapper.removeEventListener('focusin', state.onActivateTarget);
    state.wrapper.removeEventListener('pointerdown', state.onActivateTarget);

    state.nameInput.removeEventListener('blur', state.onImmediate);

    if (state.targetImage) {
      state.targetImage.removeEventListener('load', state.onImageLoad);
    }

    if (state.layer && state.layer.parentElement) {
      state.layer.parentElement.removeChild(state.layer);
    }
  }

  pruneDisconnectedStates() {
    this.states = this.states.filter((state) => {
      if (state.block.isConnected) return true;
      this.unbindState(state);
      return false;
    });
  }

  handleVariantUpdate() {
    window.requestAnimationFrame(() => {
      this.pruneDisconnectedStates();
      this.bindAllBlocks();

      this.states.forEach((state) => {
        this.refreshTarget(state, { allowAutoScroll: false });
        this.render(state);
      });
    });
  }

  handleSlideshowSelect(event) {
    const slide = event?.detail?.slide;
    if (!(slide instanceof Element)) return;

    this.states.forEach((state) => {
      if (!state.block.isConnected || !state.gallery) return;
      if (!state.gallery.contains(slide)) return;

      this.refreshTarget(state, { allowAutoScroll: false });
      this.render(state);
    });
  }

  handleResize() {
    if (this.resizeScheduled) return;
    this.resizeScheduled = true;

    window.requestAnimationFrame(() => {
      this.resizeScheduled = false;

      this.pruneDisconnectedStates();
      this.states.forEach((state) => {
        this.refreshTarget(state, { allowAutoScroll: false });
        this.render(state);
      });
    });
  }

  renderAll() {
    this.pruneDisconnectedStates();
    this.states.forEach((state) => {
      this.refreshTarget(state, { allowAutoScroll: false });
      this.render(state);
    });
  }

  scheduleDebouncedRender(state) {
    window.clearTimeout(state.debounceTimer);
    state.debounceTimer = window.setTimeout(() => {
      this.refreshTarget(state, { allowAutoScroll: false });
      this.render(state);
    }, 180);
  }

  renderNow(state) {
    window.clearTimeout(state.debounceTimer);
    this.refreshTarget(state, { allowAutoScroll: false });
    this.render(state);
  }

  handleModeChange(state, event) {
    if (event?.detail?.mode !== 'custom') return;
    this.refreshTarget(state, { allowAutoScroll: false });
    this.switchToTargetSlide(state);
    this.render(state);
  }

  switchToTargetSlide(state) {
    if (!(state.targetContainer instanceof HTMLElement)) return;
    const gridVisible = this.isElementVisible(state.gallery?.querySelector('.media-gallery__grid'));
    if (gridVisible) return;

    const slideshow = this.resolveSlideshow(state.gallery);
    if (!slideshow || typeof slideshow.select !== 'function') return;
    if (this.isTargetSlideActive(state.targetContainer)) return;

    const targetIndex = this.getTargetIndex(state.block);
    try {
      slideshow.select(targetIndex, undefined, { animate: true });
    } catch (_e) {}
  }

  handleInteractiveTargetSync(state, event) {
    const target = event?.target;
    if (!(target instanceof Element)) return;

    const actionable = target.closest(
      '.personalizador__block--nombre input, .personalizador__block--nombre textarea, .personalizador__block--bandera select'
    );
    if (!(actionable instanceof Element)) return;

    this.refreshTarget(state, { allowAutoScroll: false });
    if (!window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
      this.focusTargetImage(state);
    }
    this.render(state);
  }

  focusTargetImage(state) {
    if (!(state.targetContainer instanceof HTMLElement)) return;

    const gridVisible = this.isElementVisible(state.gallery?.querySelector('.media-gallery__grid'));
    const slideshow = this.resolveSlideshow(state.gallery);
    const targetIndex = this.getTargetIndex(state.block);
    let didSwitchSlide = false;

    if (!gridVisible && slideshow && typeof slideshow.select === 'function' && !this.isTargetSlideActive(state.targetContainer)) {
      try {
        slideshow.select(targetIndex, undefined, { animate: false });
        didSwitchSlide = true;
      } catch (_error) {
        // Continue with scroll fallback.
      }
    }

    if (didSwitchSlide || !this.isElementInViewport(state.targetContainer)) {
      window.setTimeout(() => {
        if (!(state.targetContainer instanceof HTMLElement)) return;
        state.targetContainer.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }, 40);
    }
  }

  refreshTarget(state, { allowAutoScroll }) {
    const gallery = this.resolveGallery(state.block);
    if (!(gallery instanceof HTMLElement)) {
      this.detachLayer(state);
      state.gallery = null;
      return;
    }

    state.gallery = gallery;

    const targetIndex = this.getTargetIndex(state.block);
    const slideshowContainers = Array.from(
      gallery.querySelectorAll('slideshow-component[ref="slideshow"] slideshow-slide.product-media-container')
    );
    const gridContainers = Array.from(gallery.querySelectorAll('.media-gallery__grid > .product-media-container'));

    const slideshowTarget = this.pickTargetContainer(slideshowContainers, targetIndex);
    const gridTarget = this.pickTargetContainer(gridContainers, targetIndex);
    const gridVisible = this.isElementVisible(gallery.querySelector('.media-gallery__grid'));

    const nextTarget = gridVisible ? gridTarget || slideshowTarget : slideshowTarget || gridTarget;
    if (!(nextTarget instanceof HTMLElement)) {
      this.detachLayer(state);
      return;
    }

    this.attachLayer(state, nextTarget);

    const nextImage = nextTarget.querySelector('.product-media__image');
    if (state.targetImage !== nextImage) {
      if (state.targetImage) {
        state.targetImage.removeEventListener('load', state.onImageLoad);
      }

      state.targetImage = nextImage instanceof HTMLImageElement ? nextImage : null;
      state.referenceImageWidth = null;
      if (state.targetImage) {
        state.targetImage.addEventListener('load', state.onImageLoad);
      }
    }

    if (allowAutoScroll) {
      this.autoScrollToTarget(state, targetIndex);
    }
  }

  resolveGallery(block) {
    const section = block.closest('.shopify-section');
    const scopedGallery = section ? section.querySelector('media-gallery') : null;
    if (scopedGallery instanceof HTMLElement) return scopedGallery;

    const fallbackGallery = document.querySelector('media-gallery');
    return fallbackGallery instanceof HTMLElement ? fallbackGallery : null;
  }

  resolveSlideshow(gallery) {
    if (!(gallery instanceof HTMLElement)) return null;
    if (gallery.slideshow) return gallery.slideshow;

    const slideshow = gallery.querySelector('slideshow-component[ref="slideshow"]');
    return slideshow instanceof HTMLElement ? slideshow : null;
  }

  pickTargetContainer(containers, index) {
    if (!Array.isArray(containers) || containers.length === 0) return null;

    const indexed = index >= 0 && index < containers.length ? containers[index] : null;
    if (this.isImageContainer(indexed)) return indexed;

    const firstImage = containers.find((container) => this.isImageContainer(container));
    if (firstImage) return firstImage;

    if (indexed) return indexed;
    return containers[0];
  }

  isImageContainer(container) {
    return container instanceof HTMLElement && container.classList.contains('product-media-container--image');
  }

  isElementVisible(element) {
    if (!(element instanceof Element)) return false;

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') return false;

    return element.getClientRects().length > 0;
  }

  isElementInViewport(element) {
    if (!(element instanceof Element)) return false;
    const rect = element.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const vw = window.innerWidth || document.documentElement.clientWidth;
    return rect.bottom > 0 && rect.right > 0 && rect.top < vh && rect.left < vw;
  }

  isTargetSlideActive(targetContainer) {
    if (!(targetContainer instanceof HTMLElement)) return true;
    if (targetContainer.tagName.toLowerCase() !== 'slideshow-slide') return true;
    if (targetContainer.hasAttribute('hidden')) return false;
    return targetContainer.getAttribute('aria-hidden') !== 'true';
  }

  attachLayer(state, targetContainer) {
    if (state.targetContainer !== targetContainer) {
      this.detachLayer(state);
      state.targetContainer = targetContainer;
    }

    if (!state.targetContainer) return;

    let layer = Array.from(state.targetContainer.children).find(
      (child) => child instanceof HTMLElement && child.classList.contains('personalizador-preview-layer')
    );

    if (!(layer instanceof HTMLElement)) {
      layer = document.createElement('div');
      layer.className = 'personalizador-preview-layer';
      state.targetContainer.appendChild(layer);
    }

    let canvas = layer.querySelector('canvas.personalizador-preview-canvas');
    if (!(canvas instanceof HTMLCanvasElement)) {
      canvas = document.createElement('canvas');
      canvas.className = 'personalizador-preview-canvas';
      layer.appendChild(canvas);
    }

    state.layer = layer;
    state.canvas = canvas;
  }

  detachLayer(state) {
    if (state.targetImage) {
      state.targetImage.removeEventListener('load', state.onImageLoad);
    }

    state.targetImage = null;
    state.targetContainer = null;
    state.referenceImageWidth = null;

    if (state.layer && state.layer.parentElement) {
      state.layer.parentElement.removeChild(state.layer);
    }

    state.layer = null;
    state.canvas = null;
  }

  render(state) {
    if (this.getPersonalizadorMode(state.wrapper) !== 'custom') {
      this.detachLayer(state);
      return;
    }

    if (!(state.canvas instanceof HTMLCanvasElement) || !(state.targetContainer instanceof HTMLElement)) return;

    const containerRect = state.targetContainer.getBoundingClientRect();
    const width = Math.max(1, Math.round(containerRect.width));
    const height = Math.max(1, Math.round(containerRect.height));
    if (!width || !height) return;

    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const pixelWidth = Math.round(width * dpr);
    const pixelHeight = Math.round(height * dpr);

    if (state.canvas.width !== pixelWidth || state.canvas.height !== pixelHeight) {
      state.canvas.width = pixelWidth;
      state.canvas.height = pixelHeight;
    }

    const ctx = state.canvas.getContext('2d');
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const nameText = (state.nameInput.value || '').toString().trim().toUpperCase();
    const flagState = this.resolveFlagState(state.wrapper);
    const logoUrl = this.getUploadedLogoImageUrl(state);

    if (!nameText && !flagState.url && !logoUrl) return;

    const cfg = this.readTextConfig(state.block);
    const imageBounds = this.resolveImageBounds(state, containerRect, width, height);
    const scale = this.resolveScale(state, imageBounds.width);

    if (flagState.url) {
      this.drawFlag(ctx, {
        areaX: imageBounds.x,
        areaY: imageBounds.y,
        areaWidth: imageBounds.width,
        areaHeight: imageBounds.height,
        scale,
        xPct: flagState.xPct,
        yPct: flagState.yPct,
        widthPx: flagState.widthPx,
        url: flagState.url,
      });
    }

    if (logoUrl) {
      const logoWidthPx = Math.max(1, this.toNumber(state.block.dataset.previewLogoWidthPx, 80));
      this.drawLogoImage(ctx, {
        areaX: imageBounds.x,
        areaY: imageBounds.y,
        areaWidth: imageBounds.width,
        areaHeight: imageBounds.height,
        scale,
        xPct: cfg.nameX,
        yPct: cfg.nameY,
        widthPx: logoWidthPx,
        url: logoUrl,
      });
    } else if (nameText) {
      this.drawMultilineText(ctx, {
        text: nameText,
        x: imageBounds.x + (cfg.nameX / 100) * imageBounds.width,
        y: imageBounds.y + (cfg.nameY / 100) * imageBounds.height,
        fontSize: Math.max(1, cfg.nameSize * scale),
        color: cfg.nameColor,
      });
    }
  }

  drawLogoImage(ctx, { areaX, areaY, areaWidth, areaHeight, scale, xPct, yPct, widthPx, url }) {
    const image = this.getLogoImage(url);
    if (!(image instanceof HTMLImageElement)) return;

    const drawWidth = Math.max(1, widthPx * scale);
    const aspectRatio = image.naturalWidth > 0 ? image.naturalHeight / image.naturalWidth : 1;
    const drawHeight = Math.max(1, drawWidth * aspectRatio);

    const centerX = areaX + (xPct / 100) * areaWidth;
    const centerY = areaY + (yPct / 100) * areaHeight;

    ctx.save();
    ctx.drawImage(image, centerX - drawWidth / 2, centerY - drawHeight / 2, drawWidth, drawHeight);
    ctx.restore();
  }

  getLogoImage(url) {
    if (!url) return null;

    const cached = this.logoImageCache.get(url);
    if (cached) {
      if (cached.error) return null;
      return cached.loaded ? cached.image : null;
    }

    const image = new Image();
    const record = { image, loaded: false, error: false };

    image.onload = () => {
      record.loaded = true;
      this.renderAll();
    };

    image.onerror = () => {
      record.error = true;
    };

    image.src = url;
    this.logoImageCache.set(url, record);

    return null;
  }

  getUploadedLogoImageUrl(state) {
    if (state.wrapper.dataset.nombreLogosMutex !== 'true') return null;
    if (state.wrapper.dataset.mutexChoice !== 'logo') return null;
    const urlInputs = state.wrapper.querySelectorAll('[data-logo-position-url]');
    for (const input of urlInputs) {
      if (!(input instanceof HTMLInputElement)) continue;
      const url = input.value.trim() || (input.dataset.logoPositionPreviewUrl || '').trim();
      if (url) return url;
    }
    return null;
  }

  resolveScale(state, displayedWidth) {
    if (!(displayedWidth > 0)) return 1;

    // Reference canvas width: admin-configured via metafield, or 600px default.
    // Ensures font and flag scale proportionally on all device sizes.
    const configuredRef = this.toNumber(state.block.dataset.previewReferenceCanvasPx, 0);
    const referenceWidth = configuredRef > 0 ? configuredRef : 600;
    return this.clamp(displayedWidth / referenceWidth, 0.05, 3);
  }

  resolveImageBounds(state, containerRect, containerWidth, containerHeight) {
    if (!(state.targetImage instanceof HTMLImageElement)) {
      return { x: 0, y: 0, width: containerWidth, height: containerHeight };
    }

    const imageRect = state.targetImage.getBoundingClientRect();
    const rawX = imageRect.left - containerRect.left;
    const rawY = imageRect.top - containerRect.top;
    const rawWidth = imageRect.width;
    const rawHeight = imageRect.height;

    if (rawWidth <= 0 || rawHeight <= 0) {
      return { x: 0, y: 0, width: containerWidth, height: containerHeight };
    }

    const x = this.clamp(rawX, 0, containerWidth);
    const y = this.clamp(rawY, 0, containerHeight);
    const width = Math.max(1, Math.min(rawWidth, containerWidth - x));
    const height = Math.max(1, Math.min(rawHeight, containerHeight - y));

    return { x, y, width, height };
  }
  resolveFlagState(wrapper) {
    const flagBlock = wrapper.querySelector('.personalizador__block--bandera[data-preview-flag-x-pct]');
    if (!(flagBlock instanceof HTMLElement)) {
      return { xPct: 50, yPct: 45, widthPx: 32, url: '' };
    }

    return {
      xPct: this.clamp(this.toNumber(flagBlock.dataset.previewFlagXPct, 50), 0, 100),
      yPct: this.clamp(this.toNumber(flagBlock.dataset.previewFlagYPct, 45), 0, 100),
      widthPx: Math.max(1, this.toNumber(flagBlock.dataset.previewFlagWidthPx, 32)),
      url: (flagBlock.dataset.previewFlagUrl || '').toString().trim(),
    };
  }

  drawFlag(ctx, { areaX, areaY, areaWidth, areaHeight, scale, xPct, yPct, widthPx, url }) {
    const image = this.getFlagImage(url);
    if (!(image instanceof HTMLImageElement)) return;

    const drawWidth = Math.max(1, widthPx * scale);
    const defaultAspectRatio = 3 / 4; // fallback 4:3
    const aspectRatio = image.naturalWidth > 0 ? image.naturalHeight / image.naturalWidth : defaultAspectRatio;
    const drawHeight = Math.max(1, drawWidth * aspectRatio);

    const centerX = areaX + (xPct / 100) * areaWidth;
    const centerY = areaY + (yPct / 100) * areaHeight;

    ctx.save();
    ctx.drawImage(image, centerX - drawWidth / 2, centerY - drawHeight / 2, drawWidth, drawHeight);
    ctx.restore();
  }

  getFlagImage(url) {
    if (!url) return null;

    const cached = this.flagImageCache.get(url);
    if (cached) {
      if (cached.error) return null;
      return cached.loaded ? cached.image : null;
    }

    const image = new Image();
    image.crossOrigin = 'anonymous';

    const record = {
      image,
      loaded: false,
      error: false,
    };

    image.onload = () => {
      record.loaded = true;
      this.renderAll();
    };

    image.onerror = () => {
      record.error = true;
    };

    image.src = url;
    this.flagImageCache.set(url, record);

    return null;
  }

  drawMultilineText(ctx, { text, x, y, fontSize, color }) {
    const lines = text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    if (lines.length === 0) return;

    const lineHeight = fontSize * 1.03;
    const firstLineY = y - ((lines.length - 1) * lineHeight) / 2;

    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = color;
    ctx.font = `600 ${fontSize}px sans-serif`;

    lines.forEach((line, index) => {
      ctx.fillText(line, x, firstLineY + lineHeight * index);
    });

    ctx.restore();
  }

  readTextConfig(block) {
    return {
      nameX: this.clamp(this.toNumber(block.dataset.previewNameXPct, 50), 0, 100),
      nameY: this.clamp(this.toNumber(block.dataset.previewNameYPct, 52), 0, 100),
      nameSize: Math.max(1, this.toNumber(block.dataset.previewNameSizePx, 42)),
      nameColor: this.readColor(block.dataset.previewNameColor, '#FFFFFF'),
    };
  }

  getTargetIndex(block) {
    const raw = this.toNumber(block.dataset.previewTargetIndex, 1);
    return Math.max(0, Math.floor(raw) - 1);
  }

  toNumber(value, fallback) {
    const parsed = Number.parseFloat((value || '').toString());
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  readColor(value, fallback) {
    const normalized = (value || '').toString().trim();
    return normalized || fallback;
  }

  clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  getPersonalizadorMode(wrapper) {
    if (!(wrapper instanceof HTMLElement)) return 'none';
    return wrapper.dataset.personalizadorMode === 'custom' ? 'custom' : 'none';
  }

  autoScrollToTarget(state, targetIndex) {
    if (state.hasAutoScrolled || !(state.targetContainer instanceof HTMLElement)) return;
    state.hasAutoScrolled = true;

    const slideshow = this.resolveSlideshow(state.gallery);

    if (slideshow && typeof slideshow.select === 'function') {
      try {
        slideshow.select(targetIndex, undefined, { animate: false });
      } catch (error) {
        // Fallback to scroll only.
      }
    }

    window.setTimeout(() => {
      if (!(state.targetContainer instanceof HTMLElement)) return;
      state.targetContainer.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      this.render(state);
    }, 60);
  }
}

document.addEventListener('DOMContentLoaded', function () {
  window.personalizadorPreviewCanvas = new PersonalizadorPreviewCanvas();
});
